import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { printPalletLabels } from '../../../store/store';
import { useDocLanguage } from '../docFormat';
import { readPaper, writePaper } from './shortLabelSheetDialog';
import { kindInfo, kindsFor, useLabelPreview, LabelPreview, LabelKindSelect, LabelLanguageSelect, LabelPaperToggle } from './labelPrintControls';

// Printing the pallets ticked on the Labels screen: the same options as a pallet's own record, beside a preview of exactly what comes out. A language picked here
// holds for this printing and is not remembered: every opening starts on the app's language.
export default function PalletPrintDialog({ open, onClose, ids, branchId }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const [kind, setKind] = useState('slab');
  const [paper, setPaper] = useState(readPaper);
  const [busy, setBusy] = useState(false);
  const count = (ids || []).length;
  const [lang, setLang] = useDocLanguage(open ? (ids || []).join(',') : null);

  useEffect(() => { if (open) { setBusy(false); setKind('slab'); } }, [open]);

  const T = useMemo(() => ({
    BD: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.1)',
    CTRL_BG: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
  }), [isDark]);
  const info = kindInfo(kind);
  const preview = useLabelPreview({ branchId, ids, kind, lang, paper, enabled: open });

  const pickPaper = (value) => { setPaper(value); writePaper(value); };
  const print = async () => {
    setBusy(true);
    await dispatch(printPalletLabels({ authCtx, axiosGlobal, branchId, ids, kind, lang, paper: info.usesPaper ? paper : undefined, html: preview.html }));
    setBusy(false);
    onClose();
  };

  const heading = { fontSize: '0.74rem', fontWeight: 700, mb: 0.5 };
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="lg" fullScreen={isXs}
      PaperProps={{ sx: { borderRadius: isXs ? 0 : '14px', height: isXs ? '100%' : '90vh' }, 'data-testid': 'lbl-print-dialog' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 2, sm: 2.5 }, py: { xs: 1, sm: 1.5 }, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <PrintIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', flex: 1 }}>{t('mis.lblPrintTitle')}</Typography>
        <Typography data-testid="lbl-print-count" sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>{t('mis.lblSelected', { count })}</Typography>
        <IconButton size="small" onClick={onClose} disabled={busy} aria-label={t('common.close')} sx={isXs ? { width: 44, height: 44 } : undefined}>
          <CloseIcon sx={{ fontSize: isXs ? 22 : 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, minHeight: 0, overflowY: { xs: 'auto', md: 'hidden' } }}>
        <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 1.75,
          borderInlineEnd: { md: `1px solid ${T.BD}` }, overflowY: { md: 'auto' } }}>
          <Box>
            <Typography sx={heading}>{t('mis.lblPrintKind')}</Typography>
            <LabelKindSelect value={kind} onChange={setKind} count={count} touch={isXs} sx={{ width: '100%' }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>{t(info.descKey)}</Typography>
          </Box>
          {info.usesLanguage && (
            <Box>
              <Typography sx={heading}>{t('mis.lblLanguage')}</Typography>
              <LabelLanguageSelect value={lang} onChange={setLang} touch={isXs} sx={{ width: '100%' }} />
            </Box>
          )}
          {info.usesPaper && (
            <Box>
              <Typography sx={heading}>{t('mis.plShortSheetPaper')}</Typography>
              <LabelPaperToggle value={paper} onChange={pickPaper} disabled={busy} touch={isXs} />
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                {t(paper === 'a4' ? 'mis.plShortSheetPaperHintA4' : 'mis.plShortSheetPaperHintA5')}
              </Typography>
            </Box>
          )}
          {count > 1 && kindsFor(count).length < 3 && (
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{t('mis.lblStripOneOnly')}</Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, p: { xs: 1, sm: 2 }, overflowY: { md: 'auto' } }}>
          <LabelPreview html={preview.html} loading={preview.loading} kind={kind} paper={paper} title="labels" height={{ xs: 460, md: 'calc(90vh - 190px)' }} T={T} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: { xs: 2, sm: 2.5 }, pt: 1.5, pb: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 1.5 }, borderTop: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={print} disabled={busy || !count || !preview.html} data-testid="lbl-print-go"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <PrintIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none', flex: { xs: 2, sm: 'none' }, minHeight: { xs: 48, sm: 0 } }}>
          {t('mis.lblPrintButton')}
        </Button>
      </Box>
    </Dialog>
  );
}
